import { useMemo } from "react";
import { format } from "date-fns";
import { Mail, RefreshCw } from "lucide-react";
import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useBoardPermissions } from "../hooks/useBoardPermissions";
import { Button } from "@/modern/components/ui/button";
import {
  IntegrationAlert,
  INTEGRATION_PROVIDER_LABELS,
  useIntegrationFailures,
  type IntegrationProvider,
} from "@/integrations";

const PROVIDER_FILTERS: Array<{ id: IntegrationProvider | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "openai", label: "OpenAI" },
  { id: "company_cam", label: "CompanyCam" },
];

const IntegrationHealthDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { isBoardOwner } = useBoardPermissions();
  const {
    results,
    count,
    isLoading,
    error,
    provider,
    setProvider,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    loadMore,
    refresh,
    hasMore,
  } = useIntegrationFailures();

  const emptyMessage = useMemo(() => {
    if (!isBoardOwner) {
      return "Only the board owner can view integration failure history.";
    }
    return "No integration issues recorded.";
  }, [isBoardOwner]);

  if (!isBoardOwner) {
    return (
      <div className="p-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
          <p className="text-sm text-muted-foreground">{subcategory.description}</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={refresh} disabled={isLoading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {PROVIDER_FILTERS.map((filter) => {
          const isActive = filter.id === "all" ? !provider : provider === filter.id;
          return (
            <Button
              key={filter.id}
              type="button"
              size="sm"
              variant={isActive ? "default" : "outline"}
              onClick={() => setProvider(filter.id === "all" ? undefined : filter.id)}
            >
              {filter.label}
            </Button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Start date</label>
          <input
            type="date"
            className="border border-border rounded-md px-3 py-2 text-sm bg-background"
            value={startDate ?? ""}
            onChange={(event) => setStartDate(event.target.value || undefined)}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">End date</label>
          <input
            type="date"
            className="border border-border rounded-md px-3 py-2 text-sm bg-background"
            value={endDate ?? ""}
            onChange={(event) => setEndDate(event.target.value || undefined)}
          />
        </div>
      </div>

      {error ? (
        <IntegrationAlert variant="error" title="Could not load integration history" message={error} />
      ) : null}

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr>
              <th className="text-left px-4 py-3 font-medium">Date</th>
              <th className="text-left px-4 py-3 font-medium">Provider</th>
              <th className="text-left px-4 py-3 font-medium">Message</th>
              <th className="text-left px-4 py-3 font-medium">Source</th>
              <th className="text-left px-4 py-3 font-medium">Email</th>
            </tr>
          </thead>
          <tbody>
            {results.length === 0 && !isLoading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              results.map((event) => (
                <tr key={event.id} className="border-t border-border align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                    {format(new Date(event.created_at), "MMM d, yyyy h:mm a")}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {INTEGRATION_PROVIDER_LABELS[event.provider]}
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-medium text-foreground">{event.user_message}</p>
                    {event.detail ? (
                      <details className="mt-1 text-xs text-muted-foreground">
                        <summary className="cursor-pointer">Technical details</summary>
                        <p className="mt-1 break-words">{event.detail}</p>
                      </details>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{event.source}</td>
                  <td className="px-4 py-3">
                    {event.email_sent ? <Mail className="h-4 w-4 text-primary" aria-label="Owner email sent" /> : "—"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          Showing {results.length} of {count}
        </span>
        {hasMore ? (
          <Button type="button" variant="outline" size="sm" onClick={loadMore} disabled={isLoading}>
            Load more
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default IntegrationHealthDetail;
